import { C, F, FONT, R, S, SITE_BG, BP } from "./tokens"

export const CSS = `
*{box-sizing:border-box}
/* 100vh on mobile is measured against the viewport with the browser's own
   address bar HIDDEN — but it's usually visible, so 100vh overshoots the
   real visible area. Every fixed/sticky child sized off it (the dock, the
   bottom of .scroll) ends up partly below what's actually on screen,
   unreachable by scrolling. 100dvh tracks the browser chrome as it shows/
   hides and is the actual fix; kept as a second declaration (not a
   replacement) so a browser that doesn't understand dvh silently keeps the
   vh value instead of dropping the rule. */
.app{min-height:100vh;min-height:100dvh;font-family:${FONT.display};color:${C.text};
     -webkit-font-smoothing:antialiased;position:relative;overflow:hidden}
.n{font-family:${FONT.numeric};font-variant-numeric:tabular-nums}
/* Section labels carry NO tracking. The .14em these used to have was doing
   the work of a size increase — it stretched "SA W AP RESEVWA SOU BALANS OU"
   most of the way across the screen and made a caption read as a banner.
   Fixed here at the class rather than per-label, so every caller moves at
   once: .lbl, .sec-t and .pv-tc-label are the same role in three places. */
.lbl{font-size:11px;font-weight:700;letter-spacing:normal;text-transform:uppercase;color:${C.textMute}}
.lbl.on-dark{color:${C.onDarkMute}}

/* Ambient wash. Gives the glass something to actually refract — glass over a
   flat fill reads as a grey box, which is most of why it looked cheap. */
.ambient{position:fixed;inset:0;pointer-events:none;z-index:0;
  background:
    radial-gradient(46% 34% at 12% 4%, rgba(65,198,181,.30) 0%, transparent 70%),
    radial-gradient(40% 30% at 92% 20%, rgba(94,234,212,.24) 0%, transparent 72%),
    radial-gradient(52% 40% at 74% 96%, rgba(26,157,139,.20) 0%, transparent 74%);}

.shell{position:relative;z-index:1;display:flex;min-height:100vh;min-height:100dvh}
.col{position:relative;flex:1;min-width:0;display:flex;flex-direction:column;height:100vh;height:100dvh}

/* ── Header: persistent, never remounts ─────────────────────────────────── */
/* The header floats OVER the scroller rather than sitting above it, so
   content passes beneath and frosts. A bar that content stops short of is
   just a band of background; the blur only means anything if there is
   something behind it to blur. */
.hdr{position:absolute;top:0;left:0;right:0;z-index:20;height:72px;
     display:flex;align-items:center;justify-content:space-between;gap:${S[3]}px;
     padding:0 ${S[5]}px;
     /* Opaque enough that the title holds contrast when a DARK card passes
        under it — at .72/.52 the balance figure ghosted through and fought
        the word next to it. The blur still reads; the noise does not. */
     background:linear-gradient(180deg,rgba(255,255,255,.86) 0%,rgba(238,255,251,.72) 100%);
     border-bottom:1px solid rgba(255,255,255,.55);
     backdrop-filter:blur(22px) saturate(190%);
     -webkit-backdrop-filter:blur(22px) saturate(190%);
     box-shadow:inset 0 1px 0 rgba(255,255,255,.9),0 6px 20px -14px rgba(7,40,38,.35)}
.hdr-l{display:flex;align-items:center;gap:${S[3]}px;min-width:0}
/* Rounded glass square, one per screen. Chip tier — it rides on the header
   glass, so it must not blur what is already blurred. */
.hdr-mark{width:40px;height:40px;border-radius:${R.sm}px;display:flex;align-items:center;
  justify-content:center;flex-shrink:0;backdrop-filter:none;-webkit-backdrop-filter:none;
  background:linear-gradient(158deg,rgba(255,255,255,.9) 0%,rgba(226,250,245,.72) 100%);
  border:1px solid rgba(255,255,255,.85);
  box-shadow:inset 0 1px 0 rgba(255,255,255,1),0 4px 12px -6px rgba(7,40,38,.22)}
.hdr-t{margin:0;font-size:${F.title}px;font-weight:800;letter-spacing:-.03em;color:${C.ink};white-space:nowrap}
.hdr-r{display:flex;align-items:center;gap:${S[2]}px}
.ico{width:44px;height:44px;border-radius:${R.pill}px;display:flex;align-items:center;justify-content:center;
     cursor:pointer;padding:0;flex-shrink:0}
.ico-d{width:34px;height:34px;border-radius:${R.pill}px;display:flex;align-items:center;justify-content:center;
  cursor:pointer;padding:0;border:1px solid rgba(255,255,255,.16);background:rgba(255,255,255,.10)}

.scroll{flex:1;min-height:0;overflow-y:auto;overscroll-behavior:contain;scrollbar-width:none;padding-top:72px}
.scroll::-webkit-scrollbar{display:none}
/* The single top gap between the header and the first element on EVERY
   screen. It lives here, in the shared wrapper, precisely so no page can
   drift — 8px had every screen's first heading pressed against the bar. */
/* min-height so a short page still owns the full scroll area. Children stay
   top-aligned (justify-content is flex-start), so this changes nothing on any
   screen that does not opt in — it only gives the ones that want to fill or
   centre something real height to do it against. */
.page{padding:${S[6]}px ${S[5]}px calc(104px + env(safe-area-inset-bottom));
      display:flex;flex-direction:column;gap:${S[6]}px;max-width:560px;margin:0 auto;width:100%;
      min-height:100%}
.stack{display:flex;flex-direction:column;gap:${S[6]}px}
.steps-vp{overflow-x:clip}

/* ── Hero ───────────────────────────────────────────────────────────────── */
.hero{border-radius:${R.xl}px;padding:${S[5]}px;color:${C.onDark};display:flex;flex-direction:column;gap:${S[1]}px}
.hero-top{display:flex;align-items:center;justify-content:space-between;gap:${S[3]}px}
.hero-ctl{display:flex;align-items:center;gap:${S[2]}px}
.hero-num{font-family:${FONT.numeric};font-size:${F.hero}px;font-weight:800;letter-spacing:-.04em;
          color:${C.onDark};line-height:1.1;margin-top:${S[1]}px;min-height:46px}
.hero-sub{font-family:${FONT.numeric};font-size:12px;color:${C.onDarkSub}}
/* ── Embossed / raised ───────────────────────────────────────────────────
   One pair of recipes, reused: pressed INTO a surface, or raised OUT of it.
   The dock's home button, the active side of every pill and the hero action
   circles are the same two elements in three places — not three that
   resemble each other. */
.emboss{
  background:linear-gradient(158deg,rgba(255,255,255,.17) 0%,rgba(255,255,255,.06) 100%);
  border:1px solid rgba(255,255,255,.15);border-top-color:rgba(255,255,255,.30);
  box-shadow:inset 0 2px 5px rgba(0,0,0,.30),inset 0 -1px 0 rgba(255,255,255,.14),
             inset 0 1px 0 rgba(255,255,255,.12);
}
.raised{
  background:linear-gradient(150deg,${C.teal} 0%,${C.tealDeep} 100%);
  border:1px solid transparent;color:#fff;
  box-shadow:0 6px 14px -6px rgba(10,96,85,.85),inset 0 1px 0 rgba(255,255,255,.30),
             inset 0 -2px 4px rgba(0,0,0,.18);
}

.hero-acts{display:flex;gap:${S[6]}px;margin-top:${S[5]}px;justify-content:center}
/* Icon only — the glyph already said what the label repeated. 52px, well
   clear of the 44px minimum. */
/* All three identical: a raised gradient disc with a white glyph. The
   highlight sits top-left and the shadow bottom-right, so it reads as a
   physical button rather than a filled circle. */
.act{width:56px;height:56px;border-radius:${R.pill}px;display:inline-flex;align-items:center;
     justify-content:center;cursor:pointer;padding:0;flex-shrink:0;color:#fff;
     background:linear-gradient(145deg,${C.tealBright} 0%,${C.teal} 42%,${C.tealDeep} 100%);
     border:1px solid rgba(255,255,255,.2);
     box-shadow:
       inset 0 2px 3px rgba(255,255,255,.42),
       inset 0 -3px 6px rgba(0,0,0,.26),
       0 10px 22px -10px rgba(4,32,28,.85)}
.act-solid{color:#fff}

/* Rechaje — the one labelled action on the hero, so it is a row rather than a
   fourth circle. It uses the same emboss/raise pair as everything else on this
   card: the row is a well pressed INTO the dark surface, the icon is a teal
   disc raised OUT of it, which ties it to the three circles above without
   repeating them. */
.topup{display:flex;align-items:center;gap:${S[3]}px;width:100%;margin-top:${S[4]}px;
  padding:10px 12px;border-radius:${R.lg}px;cursor:pointer;text-align:left;
  font-family:${FONT.display};font-size:14.5px;font-weight:700;color:${C.onDark};
  background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.16);
  backdrop-filter:blur(14px) saturate(160%);-webkit-backdrop-filter:blur(14px) saturate(160%);
  box-shadow:inset 0 1px 0 rgba(255,255,255,.10),inset 0 -1px 0 rgba(0,0,0,.16);
  transition:background .2s ease}
.topup:hover{background:rgba(255,255,255,.13)}
.topup-ico{width:34px;height:34px;border-radius:12px;flex-shrink:0;
  display:flex;align-items:center;justify-content:center}
/* Takes the slack so the chevron is pinned right instead of trailing the
   label — a centred label with a floating chevron was most of why this read
   as unstyled. */
.topup-lbl{flex:1;min-width:0;letter-spacing:-.01em}

.seg{display:flex;padding:4px;border-radius:${R.pill}px;gap:2px}
/* On the dark hero the segment inverts: a translucent dark well with a mint
   fill, rather than a white chip that fights the surface it sits on. */
.hero .seg{background:rgba(255,255,255,.07);
  border:1px solid rgba(255,255,255,.16);
  backdrop-filter:blur(14px) saturate(160%);-webkit-backdrop-filter:blur(14px) saturate(160%);
  box-shadow:inset 0 1px 0 rgba(255,255,255,.14)}
/* Inactive is plain muted text on the glass — no plate, no border. */
.hero .seg-b{color:rgba(190,214,210,.72);font-weight:800;letter-spacing:.01em}
/* White on the green, per the reference — dark text on that green loses
   contrast at this size and reads as a disabled chip. */
.hero .seg-b.on{color:#fff}
.hero .seg-fill{background:#22BCA0;
  box-shadow:0 5px 14px -5px rgba(0,0,0,.5),inset 0 1px 0 rgba(255,255,255,.28)}
.seg-b{position:relative;border:none;background:none;cursor:pointer;border-radius:${R.pill}px;
  padding:7px 15px;font-family:${FONT.numeric};font-size:12px;font-weight:700;color:${C.textSub};
  min-height:32px}
/* Scoped away from the fill. A ".seg-b span" descendant selector (0,1,1)
   out-specifies ".seg-fill" (0,1,0), so it forced position:relative onto the
   fill — which then collapsed to 0x0 as a flex item and the green pill
   vanished. The hero pill only survived because ".hero .seg-fill" had the
   specificity to win. Backticks are illegal in this comment: the whole
   stylesheet is a template literal. */
.seg-b > span:not(.seg-fill){position:relative;z-index:1}
.seg-b.on{color:#fff}
/* Same raised gradient as the dock home button, verbatim. */
/* Active side: ONE flat brand-green pill with bold WHITE text. Flat, not a
   gradient — the reference has no visible ramp across it, and a gradient at
   this size just muddies the green. Same element the VIT Wi/Non toggle and
   the avatar selector use. */
.seg-fill{position:absolute;inset:0;border-radius:${R.pill}px;z-index:0;
  background:#20B79C;
  box-shadow:0 4px 12px -4px rgba(10,96,85,.55),inset 0 1px 0 rgba(255,255,255,.22)}
.seg-b:disabled{opacity:.45;cursor:default}

/* ── Stats ──────────────────────────────────────────────────────────────── */
.stats{display:grid;grid-template-columns:repeat(3,1fr);gap:${S[2]}px}
.stat{border-radius:${R.lg}px;padding:${S[3]}px;display:flex;flex-direction:column;gap:${S[1]}px;min-width:0}
.stat-ico{width:26px;height:26px;border-radius:${R.xs}px;display:flex;align-items:center;justify-content:center;
  background:rgba(26,157,139,.12);border:1px solid rgba(26,157,139,.16);margin-bottom:${S[1]}px}
.stat-v{font-family:${FONT.numeric};font-size:${F.sub}px;font-weight:800;letter-spacing:-.03em;color:${C.text};
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.vit-bar{display:flex;gap:3px;margin-top:${S[1]}px}
.vit-seg{flex:1;height:3px;border-radius:${R.pill}px;transform-origin:left}

/* ── Sections & lists ───────────────────────────────────────────────────── */
.sec{display:flex;flex-direction:column;gap:${S[3]}px}
.sec-hd{display:flex;align-items:center;justify-content:space-between;gap:${S[3]}px}
.sec-t{margin:0;font-size:11px;font-weight:700;letter-spacing:normal;text-transform:uppercase;color:${C.textMute}}
.link{border:none;background:none;cursor:pointer;display:inline-flex;align-items:center;gap:4px;
  font-family:${FONT.display};font-size:12.5px;font-weight:600;color:${C.teal};padding:0}
.list{border-radius:${R.lg}px;overflow:hidden;display:flex;flex-direction:column}
.row{display:flex;align-items:center;gap:${S[3]}px;padding:${S[3]}px ${S[4]}px;cursor:pointer;
     border-bottom:1px solid ${C.hairline};text-align:left;width:100%;background:none;border-left:none;
     border-right:none;border-top:none;font-family:${FONT.display}}
.list>div:last-child .row,.list>.row:last-child{border-bottom:none}
.row-av{flex-shrink:0;display:flex}
.row-ico{width:40px;height:40px;border-radius:${R.sm}px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.row-mid{flex:1;min-width:0}
.row-nm{font-size:14px;font-weight:600;color:${C.text};overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.row-sub{font-family:${FONT.numeric};font-size:11.5px;color:${C.textMute};margin-top:1px;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.row-end{display:flex;flex-direction:column;align-items:flex-end;gap:5px;flex-shrink:0}
.row-amt{font-family:${FONT.numeric};font-size:14px;font-weight:800;color:${C.ok};letter-spacing:-.02em}
.row-amt.out{color:${C.text}}
.empty{padding:${S[10]}px ${S[5]}px;display:flex;flex-direction:column;align-items:center;gap:${S[3]}px;
  color:${C.textMute};font-size:13.5px;text-align:center}

.pill{display:inline-flex;align-items:center;gap:4px;padding:3px 9px;border-radius:${R.pill}px;
  font-size:10px;font-weight:700;letter-spacing:.03em;white-space:nowrap}
.pill-ok{background:${C.okSoft};color:${C.ok}}
.pill-warn{background:${C.warnSoft};color:${C.warn}}
.pill-danger{background:${C.dangerSoft};color:${C.danger}}
.pill-info{background:rgba(26,157,139,.13);color:${C.tealDeep}}

.av{border-radius:${R.pill}px;display:flex;align-items:center;justify-content:center;color:#fff;
    font-weight:800;font-family:${FONT.numeric};flex-shrink:0;letter-spacing:-.02em}

/* ── Fields, chips, CTAs ────────────────────────────────────────────────── */
.field{display:flex;align-items:center;gap:${S[3]}px;padding:0 ${S[4]}px;height:52px;border-radius:${R.md}px}
.field input{flex:1;min-width:0;border:none;background:none;outline:none;font-family:${FONT.numeric};
  /* 16px stops iOS zooming the page on focus. */
  font-size:16px;color:${C.text}}
.field input::placeholder{color:${C.textMute};font-family:${FONT.display};font-size:14px}
.field-suffix{font-family:${FONT.numeric};font-size:12px;font-weight:700;color:${C.textMute}}
.fld{display:flex;flex-direction:column;gap:${S[2]}px}
.hint{margin:0;font-size:11.5px;color:${C.textMute};line-height:1.45}

/* ── Step 2 brief — what to send, before how to send it ─────────────────── */
.pb{display:flex;flex-direction:column;gap:${S[2]}px;padding:${S[4]}px;border-radius:${R.lg}px}
.pb-big{font-family:${FONT.numeric};font-size:${F.display}px;font-weight:800;letter-spacing:-.035em;
  color:${C.ink};line-height:1.15;margin-top:2px}
.pb-big span{font-size:16px;font-weight:700;color:${C.textMute};letter-spacing:0}
.pb-rows{display:flex;flex-direction:column;margin-top:${S[2]}px;padding-top:${S[3]}px;
  border-top:1px solid ${C.hairline}}
.pb-row{display:flex;align-items:center;justify-content:space-between;gap:${S[3]}px;
  padding:6px 0;font-size:13.5px;color:${C.textSub}}
.pb-row .n{font-weight:800;color:${C.text}}
.pb-row.ok,.pb-row.ok .n{color:${C.ok}}
/* Three controls share this row once VIT is on, which overflows 430px by a
   few pixels. Wrapping drops the controls to their own line rather than
   breaking the label across two. */
.pb-vit{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;
  gap:${S[2]}px ${S[3]}px;margin-top:${S[2]}px;padding-top:${S[3]}px;
  border-top:1px solid ${C.hairline}}
.pb-vitk{display:flex;align-items:center;gap:${S[2]}px;font-size:13.5px;font-weight:700;
  color:${C.text};white-space:nowrap}
/* Two controls on one line: what the VIT is spent on, then whether it is
   spent at all. overflow:hidden lets the − / + pair collapse its width when
   VIT is off instead of vanishing and snapping the row. */
.pb-vitctl{display:flex;align-items:center;gap:${S[2]}px;flex-shrink:0}
.seg-sm{overflow:hidden;flex-shrink:0}
.seg-sm .seg-b{padding:6px 11px;min-width:0}

/* ── Verification (step 2) ──────────────────────────────────────────────── */
.vfy{display:flex;flex-direction:column;gap:${S[3]}px;padding:${S[4]}px;border-radius:${R.lg}px}
.vfy-top{display:flex;align-items:center;gap:${S[4]}px}
.vfy-qr{width:104px;height:104px;flex-shrink:0;border-radius:14px;padding:7px;background:#fff;
  box-shadow:0 8px 20px -12px rgba(7,40,38,.5);border:1px solid rgba(255,255,255,.9)}
.vfy-qr img{width:100%;height:100%;display:block;image-rendering:pixelated;border-radius:8px}
.vfy-acct{min-width:0;display:flex;flex-direction:column;gap:5px}
.vfy-handle{display:flex;align-items:center;gap:${S[3]}px}
.vfy-mark{width:38px;height:38px;border-radius:12px;flex-shrink:0;display:flex;align-items:center;
  justify-content:center;background:rgba(255,255,255,.72);border:1px solid rgba(255,255,255,.92)}
.vfy-num{font-size:${F.sub}px;font-weight:800;letter-spacing:-.01em;color:${C.text};
  overflow-wrap:anywhere;line-height:1.25}
/* Rails now ask for two things, so each one has to say which it is. Without
   the labels the screenshot and the number read as alternatives. */
.vfy-lbl{display:flex;align-items:center;gap:${S[2]}px;margin-top:${S[2]}px;
  font-size:12.5px;font-weight:700;color:${C.textSub}}
.vfy-opt{font-size:9.5px;font-weight:800;letter-spacing:.07em;text-transform:uppercase;
  padding:3px 7px;border-radius:${R.pill}px;color:${C.textMute};background:rgba(11,46,44,.06);border:1px solid rgba(11,46,44,.06)}
/* A plain red asterisk reads as "required" without needing its own pill —
   the same convention every standard form uses. */
.vfy-star{color:${C.danger};font-weight:800;font-size:14px;line-height:1}
.vfy-txn{margin-top:${S[1]}px}
/* Tabular so the counter does not jitter as digits are typed. */
.vfy-count{font-size:11.5px;font-weight:800;color:${C.textMute};flex-shrink:0}
.vfy-count[data-ok="true"]{color:${C.ok}}
.vfy-err{display:flex;align-items:center;gap:7px;font-size:12px;font-weight:600;color:${C.danger}}
.vfy-drop{cursor:pointer;transition:border-color .18s ease,background .18s ease}
.vfy-drop[data-drag="true"]{border-color:${C.teal};background:rgba(26,157,139,.10)}
.vfy-drop[data-has="true"]{border-style:solid;border-color:rgba(26,157,139,.34);
  background:rgba(26,157,139,.07)}
.vfy-drop .drop-t{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.vfy-x{width:28px;height:28px;border-radius:${R.pill}px;flex-shrink:0;display:flex;align-items:center;
  justify-content:center;border:1px solid ${C.hairline};background:rgba(255,255,255,.7);cursor:pointer;padding:0}

/* ── Tracker — where the money is ───────────────────────────────────────── */
.trk-hd{display:flex;align-items:center;justify-content:space-between;gap:${S[3]}px;
  margin-top:${S[4]}px}
.trk-pill{display:inline-flex;align-items:center;gap:${S[2]}px;padding:6px 12px;
  border-radius:${R.pill}px;font-size:12.5px;font-weight:700;white-space:nowrap;
  border:1px solid transparent;font-family:${FONT.numeric};min-width:0}
.trk-dot{width:8px;height:8px;border-radius:${R.pill}px;flex-shrink:0}
/* The live dot breathes a ring outward, the same 2.4s pulse the dashboard
   uses. It marks "still moving" without a spinner, which on a money screen
   reads as waiting rather than as loading. */
.trk-dot.beat{animation:trk-pulse 2.4s ease-in-out infinite}
@keyframes trk-pulse{
  0%,100%{box-shadow:0 0 0 0 rgba(26,157,139,.42)}
  50%{box-shadow:0 0 0 6px rgba(26,157,139,0)}
}
.trk-bars{display:inline-flex;align-items:center;gap:3px;margin-left:2px;flex-shrink:0}
.trk-bar{width:11px;height:3px;border-radius:${R.pill}px;transform-origin:left center}
.trk{display:flex;flex-direction:column;border-radius:${R.lg}px;padding:${S[4]}px}
.trk-row{display:flex;align-items:flex-start;gap:${S[3]}px;min-height:56px}
/* The rail and its node are one column, so the connector always meets the
   circle regardless of how tall a row's text runs. */
.trk-mark{position:relative;display:flex;flex-direction:column;align-items:center;
  align-self:stretch;flex-shrink:0;width:30px}
.trk-node{width:30px;height:30px;border-radius:${R.pill}px;flex-shrink:0;border:1.5px solid;
  display:flex;align-items:center;justify-content:center}
.trk-node.beat{animation:trk-pulse 2.4s ease-in-out infinite}
.trk-line{flex:1;width:2px;border-radius:${R.pill}px;margin:2px 0}
.trk-mid{flex:1;min-width:0;display:flex;flex-direction:column;gap:2px;padding-top:4px}
.trk-l{font-size:14.5px;font-weight:700;color:${C.text}}
.trk-at{font-family:${FONT.numeric};font-size:12px;color:${C.textMute}}
.trk-word{font-size:12.5px;font-weight:700;flex-shrink:0;padding-top:6px}
.trk-help{display:flex;align-items:center;justify-content:center;gap:${S[2]}px;width:100%;
  margin-top:${S[2]}px;padding:13px;border-radius:${R.md}px;cursor:pointer;border:none;
  font-family:${FONT.display};font-size:14.5px;font-weight:700;color:${C.tealDeep};
  background:rgba(255,255,255,.66);box-shadow:inset 0 1px 0 rgba(255,255,255,.9)}
/* Colour comes from the component, which knows whether this is a failure or
   a delay — the two must not look the same. */
.trk-note{display:flex;gap:${S[2]}px;margin-top:${S[3]}px;padding:13px 15px;
  border-radius:${R.md}px;border:1px solid transparent}
.trk-note span{flex:1;min-width:0;font-family:${FONT.numeric};font-size:12.5px;
  line-height:1.5;color:${C.textSub}}
.trk-note strong{display:block;font-family:${FONT.display};font-size:13.5px;
  margin-bottom:3px}

/* ── Receipt actions ────────────────────────────────────────────────────── */
.rcp{display:flex;flex-direction:column;gap:${S[2]}px}
.rcp .cta{flex:1 1 auto;width:100%}
/* Compound selector on purpose. This block sits ABOVE the .cta base rule in
   the sheet, so a bare .cta-plain would lose the cascade to it on every
   property they share and render as a filled primary button. */
.cta.cta-plain{background:rgba(255,255,255,.5);color:${C.textSub};
  border:1px solid ${C.hairline};box-shadow:none;font-weight:700}
/* Copy row: the whole row is the target, and the glyph confirms in place. */
.rev-row.rev-copy{cursor:pointer;min-height:44px;align-items:center;
  margin:-2px -${S[2]}px;padding:2px ${S[2]}px;border-radius:${R.sm}px}
.rev-copy .rev-v{display:inline-flex;align-items:center;gap:6px}

/* ── Step 3 · Konfime summary ───────────────────────────────────────────── */
.kf{display:flex;flex-direction:column;gap:${S[4]}px}
.kf-hd{display:flex;flex-direction:column;align-items:center;text-align:center;gap:${S[2]}px;
  padding:${S[2]}px 0 ${S[3]}px}
.kf-av{position:relative;isolation:isolate;display:flex;flex-shrink:0;
  filter:drop-shadow(0 14px 28px rgba(7,40,38,.28))}
.kf-av img{border-radius:${R.pill}px;display:block}
/* The badge overlaps the avatar's lower-right; the ring is the page colour so
   it punches cleanly through the portrait edge. */
.kf-badge{position:absolute;right:-2px;bottom:2px;width:34px;height:34px;border-radius:${R.pill}px;
  display:flex;align-items:center;justify-content:center;z-index:2;
  background:linear-gradient(150deg,${C.teal} 0%,${C.tealDeep} 100%);
  box-shadow:0 0 0 4px ${SITE_BG},0 6px 14px -8px rgba(7,40,38,.7)}
.kf-t{margin:${S[2]}px 0 0;font-size:${F.display}px;font-weight:800;letter-spacing:-.035em;color:${C.ink}}
.kf-p{margin:0;max-width:30ch;font-size:14.5px;line-height:1.5;color:${C.textSub}}

.kf-card{border-radius:${R.lg}px;padding:${S[2]}px ${S[4]}px}
.kf-row{display:flex;align-items:center;justify-content:space-between;gap:${S[4]}px;
  min-height:52px;padding:${S[2]}px 0}
.kf-k{display:flex;align-items:center;gap:${S[3]}px;flex-shrink:0;
  font-size:14.5px;color:${C.textSub}}
.kf-v{min-width:0;text-align:right;font-size:15px;font-weight:800;letter-spacing:-.01em;
  color:${C.text};overflow-wrap:anywhere}
.kf-node{display:flex;align-items:center;justify-content:flex-end;gap:${S[3]}px;min-width:0}
.kf-vit{display:flex;align-items:center;gap:${S[3]}px}
.kf-vitn{font-size:13px;font-weight:800;color:${C.ok}}
.kf-div{height:1px;background:${C.hairline};margin:${S[2]}px 0}

.kf-ref{display:flex;align-items:center;justify-content:space-between;gap:${S[4]}px;
  border-radius:${R.lg}px;padding:${S[4]}px}
.kf-refnum{font-size:${F.sub}px;font-weight:800;letter-spacing:-.01em;color:${C.ink};margin-top:3px;
  overflow-wrap:anywhere}
.kf-copy{display:flex;align-items:center;gap:${S[2]}px;flex-shrink:0;cursor:pointer;
  height:44px;padding:0 ${S[4]}px;border-radius:${R.pill}px;
  font-family:${FONT.display};font-size:14.5px;font-weight:800;color:${C.tealDeep};
  background:rgba(255,255,255,.8);border:1px solid rgba(255,255,255,.95);
  box-shadow:inset 0 1px 0 rgba(255,255,255,.95),0 6px 16px -10px rgba(7,40,38,.4)}
.kf-note{margin:0;text-align:center;font-size:12.5px;line-height:1.5;color:${C.textMute};
  padding:0 ${S[2]}px}

/* The reason the button is disabled, said out loud rather than left to be
   guessed at from a grey rectangle. */
.blocked{display:flex;align-items:center;gap:${S[2]}px;padding:11px 13px;border-radius:${R.md}px;
  font-size:12.5px;font-weight:600;line-height:1.4;color:${C.warn};
  background:rgba(214,158,46,.12);border:1px solid rgba(214,158,46,.28)}
.drop{display:flex;align-items:center;gap:${S[3]}px;padding:${S[4]}px;border-radius:${R.md}px;
  cursor:pointer;border-style:dashed !important;min-height:64px}
.drop-t{font-size:13.5px;font-weight:600;color:${C.text}}
.drop-s{font-size:11.5px;color:${C.textMute};margin-top:2px}
.chips{display:flex;gap:${S[2]}px;flex-wrap:wrap}
.chip{position:relative;border-radius:${R.pill}px;padding:9px 15px;cursor:pointer;
  font-family:${FONT.numeric};font-size:13px;font-weight:700;color:${C.textSub}}
.chip.on{color:#fff}
.chip-fill{position:absolute;inset:0;border-radius:${R.pill}px;background:${C.teal};z-index:0}
.chip.on:not(:has(.chip-fill)){background:${C.teal};border-color:transparent}
.cta{flex:1;display:inline-flex;align-items:center;justify-content:center;gap:${S[2]}px;
  padding:${S[4]}px ${S[5]}px;border-radius:${R.md}px;border:none;cursor:pointer;
  font-family:${FONT.display};font-size:15px;font-weight:700;color:#fff;
  background:linear-gradient(150deg,${C.teal} 0%,${C.tealDeep} 100%);
  box-shadow:0 10px 24px -12px rgba(10,96,85,.75),inset 0 1px 0 rgba(255,255,255,.22)}
.cta:disabled{opacity:.42;cursor:default;box-shadow:none}
.cta-ghost{background:rgba(255,255,255,.6);color:${C.text};border:1px solid ${C.hairline};box-shadow:none;flex:0 0 auto}
.btn-row{display:flex;gap:${S[2]}px}
.spin{width:15px;height:15px;border-radius:${R.pill}px;border:2px solid rgba(255,255,255,.35);
  border-top-color:#fff;animation:sp .7s linear infinite}
@keyframes sp{to{transform:rotate(360deg)}}

/* ── Converter — PORTED VERBATIM from transfe_flow_recent.tsx ────────────
   Same class names, same values. This is the pattern users already know from
   the dashboard, so anything that differed here was a bug, not a decision. */
.pv-tc-stack{display:flex;flex-direction:column;gap:0}
.pv-tc-slot{min-width:0;display:flex;flex-direction:column}
.pv-tc-card{position:relative;border-radius:28px;padding:18px 20px 16px;display:flex;
  flex-direction:column;gap:10px}
.pv-tc-cardhead{display:flex;align-items:center;justify-content:space-between;gap:10px;min-height:22px}
.pv-tc-label{font-size:11px;font-weight:700;letter-spacing:normal;text-transform:uppercase;color:${C.textSub}}
.pv-tc-cardbody{display:flex;align-items:center;gap:12px;min-height:56px}
.pv-tc-amount{flex:1;min-width:0;border:none;background:transparent;outline:none;padding:0;margin:0;
  font-family:${FONT.numeric};font-size:${F.hero}px;font-weight:800;letter-spacing:-.035em;
  color:${C.text};font-variant-numeric:tabular-nums;line-height:1.1}
.pv-tc-amount-out{display:flex;align-items:center;overflow:hidden}
.pv-tc-zero{color:rgba(11,46,44,.22)}
.pv-tc-num{font-variant-numeric:tabular-nums}
.pv-tc-cardfoot{display:flex;align-items:center;justify-content:space-between;gap:10px;
  padding-top:10px;border-top:1px solid ${C.hairline}}
.pv-tc-foothint{font-size:11.5px;color:${C.textMute}}
.pv-tc-footval{font-size:12.5px;font-weight:700;color:${C.textSub}}
.pv-tc-pill{flex-shrink:0;display:flex;align-items:center;gap:9px;cursor:pointer;
  height:46px;padding:0 13px 0 11px;border-radius:999px;
  background:rgba(255,255,255,.72);border:1px solid rgba(255,255,255,.92);
  box-shadow:inset 0 1px 0 rgba(255,255,255,.95),0 6px 16px -10px rgba(7,40,38,.4);
  font-family:${FONT.display}}
.pv-tc-pillname{font-size:14.5px;font-weight:700;color:${C.text}}

/* Swap: negative margins pull the row INTO the gap between the cards, and the
   ring in the page colour punches it through both. */
.pv-tc-swaprow{display:flex;align-items:center;gap:12px;margin:-14px 0;position:relative;
  z-index:3;padding:0 4px}
.pv-tc-swapline{flex:1;height:1px;background:${C.hairline}}
.pv-tc-swapbtn{width:52px;height:52px;border-radius:999px;flex-shrink:0;cursor:pointer;border:none;
  display:flex;align-items:center;justify-content:center;padding:0;
  background:linear-gradient(150deg,${C.teal} 0%,${C.tealDeep} 100%);
  box-shadow:0 12px 26px -12px rgba(10,96,85,.9),0 0 0 5px ${SITE_BG}}

.pv-tc-pickerwrap{border-radius:20px;padding:6px;margin-top:10px}
.pv-tc-picker{display:flex;gap:3px}
.pv-tc-pickopt{position:relative;flex:1;min-width:0;border:none;background:transparent;cursor:pointer;
  border-radius:16px;padding:11px 6px;font-family:${FONT.display}}
.pv-tc-pickfill{position:absolute;inset:0;border-radius:16px;background:rgba(255,255,255,.92);
  box-shadow:0 6px 16px -8px rgba(7,40,38,.35);border:1px solid rgba(255,255,255,1)}
.pv-tc-pickinner{position:relative;z-index:1;display:flex;flex-direction:column;align-items:center;gap:6px}
.pv-tc-pickmark{display:flex;opacity:.55;transition:opacity .22s ease}
.pv-tc-pickmark[data-active="true"]{opacity:1}
.pv-tc-picklabel{font-size:11.5px;font-weight:700;color:${C.textSub};overflow:hidden;
  text-overflow:ellipsis;white-space:nowrap;max-width:100%}

.pv-tc-rate{display:flex;align-items:center;gap:8px;align-self:center;padding:8px 14px;
  border-radius:999px;background:rgba(26,157,139,.10);border:1px solid rgba(26,157,139,.20);
  font-size:12.5px;font-weight:700;color:${C.tealDeep}}
.pv-tc-ratetag{font-size:10px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;
  background:linear-gradient(150deg,${C.teal},${C.tealDeep});color:#fff;padding:3px 8px;border-radius:999px}

/* The two glass tiers the converter uses. Kept as their own classes rather
   than folded into .g-card so the ported markup reads identically to the
   original — same class list, same order. */
.pv-tc-glass{
  background:linear-gradient(158deg,rgba(255,255,255,.82) 0%,rgba(236,253,250,.58) 100%);
  border:1px solid rgba(255,255,255,.86);
  backdrop-filter:blur(22px) saturate(180%);-webkit-backdrop-filter:blur(22px) saturate(180%);
  box-shadow:inset 0 1px 0 rgba(255,255,255,.95),0 22px 46px -26px rgba(7,40,38,.30);
}
.pv-tc-glass-lite{
  background:linear-gradient(158deg,rgba(255,255,255,.64) 0%,rgba(238,254,251,.40) 100%);
  border:1px solid rgba(255,255,255,.7);
  backdrop-filter:blur(16px) saturate(170%);-webkit-backdrop-filter:blur(16px) saturate(170%);
  box-shadow:inset 0 1px 0 rgba(255,255,255,.7),0 10px 26px -16px rgba(7,40,38,.28);
}
.pv-tc-amount::placeholder{color:rgba(11,46,44,.22)}
.pv-tc-amount:focus-visible{outline:none}
.pv-tc-card:focus-within{
  border-color:rgba(26,157,139,.45);
  box-shadow:inset 0 1px 0 rgba(255,255,255,.95),0 0 0 4px rgba(26,157,139,.14),
             0 22px 46px -22px rgba(7,40,38,.30);
}
.pv-tc-pill{backdrop-filter:blur(14px) saturate(170%);-webkit-backdrop-filter:blur(14px) saturate(170%);
  transition:background .2s ease}
.pv-tc-pill:hover{background:rgba(255,255,255,.9)}
.pv-tc-pillname{letter-spacing:-.01em;white-space:nowrap}
.pv-tc-foothint{font-family:${FONT.numeric}}
.pv-tc-num{font-feature-settings:"tnum" 1}

/* Side by side once there is room — the swap column stands the rule and the
   button up vertically instead of laying them across.

   The flow file trips this at BP.desktop, but the flow file is a full-width
   page. Here the same converter sits inside a shell that already spends ~420px
   on the sidebar, so tripping at 1024 gives each column ~280px and the amount
   runs under the provider pill. BP.wide gives the columns the width the
   original actually gets at 1024 — same geometry, measured from the converter
   rather than from the window. */
@media (min-width:${BP.wide}px){
  .pv-tc-stack{display:grid;grid-template-columns:minmax(0,1fr) auto minmax(0,1fr);
    align-items:start;gap:0}
  .pv-tc-slot-pay{grid-column:1}
  .pv-tc-swaprow{grid-column:2;align-self:stretch;flex-direction:column;margin:0 -16px;padding:0}
  .pv-tc-slot-receive{grid-column:3}
  .pv-tc-swapline{width:1px;height:auto;flex:1}
  .pv-tc-card{padding:22px 24px 18px}
  .pv-tc-amount{font-size:38px}
}

/* ── Step rail ──────────────────────────────────────────────────────────── */
.rail-steps{display:flex;align-items:center;justify-content:center;gap:${S[1]}px}
.stp{display:flex;align-items:center;gap:${S[2]}px;flex:0 0 auto}
.stp-n{position:relative;width:24px;height:24px;border-radius:${R.pill}px;display:flex;align-items:center;
  justify-content:center;background:rgba(11,46,44,.07)}
.stp-fill{position:absolute;inset:0;border-radius:${R.pill}px;background:${C.teal}}
.stp-num{position:relative;z-index:1;font-family:${FONT.numeric};font-size:11.5px;font-weight:800;color:${C.textMute};
  display:flex;align-items:center;justify-content:center}
.stp.on .stp-num,.stp.done .stp-num{color:#fff}
.stp.done .stp-n{background:${C.tealDeep}}
.stp-l{font-size:12px;font-weight:600;color:${C.textMute}}
.stp.on .stp-l{color:${C.text}}
.stp-bar{width:18px;height:1.5px;background:${C.hairline};margin:0 ${S[1]}px;border-radius:${R.pill}px}
@media (max-width:400px){.stp-l{display:none}.stp-bar{width:26px}}

/* ── Recipient bubbles ──────────────────────────────────────────────────── */
/* The selection ring sits at inset:-4px, so it extends past the avatar on
   every side including the top — and overflow-x:auto makes this a scroll
   container, which clips BOTH axes. 2px of top padding cut the ring and the
   crown of the circle with it. Padding now clears the ring all round; the
   negative margin keeps the row's optical alignment unchanged. */
.bubs{display:flex;gap:${S[3]}px;overflow-x:auto;padding:8px 6px ${S[2]}px;
  margin:-8px -6px 0;scrollbar-width:none}
.bubs::-webkit-scrollbar{display:none}
.bub{border:none;background:none;cursor:pointer;padding:0;display:flex;flex-direction:column;
  align-items:center;gap:6px;width:60px;flex-shrink:0;font-family:${FONT.display}}
.bub-av{position:relative;display:flex;isolation:isolate}
.bub-ring{position:absolute;inset:-4px;border-radius:${R.pill}px;background:${C.teal};z-index:-1;
  box-shadow:0 0 0 3px rgba(26,157,139,.2)}
.bub-add{width:54px;height:54px;border-radius:${R.pill}px;border:1.5px dashed rgba(11,46,44,.2);
  display:flex;align-items:center;justify-content:center}
.bub-n{font-size:11px;color:${C.textSub};max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}

/* ── Review ─────────────────────────────────────────────────────────────── */
.rev{border-radius:${R.lg}px;padding:${S[5]}px;display:flex;flex-direction:column;gap:${S[4]}px}
.rev-hd{display:flex;align-items:center;gap:${S[3]}px}
.rev-nm{font-size:15px;font-weight:700;color:${C.text}}
.rev-hn{font-family:${FONT.numeric};font-size:12px;color:${C.textMute}}
.rev-rows{display:flex;flex-direction:column;gap:${S[3]}px}
/* When the rows ARE the card, they need the card's own inset — 16px, the same
   as .trk directly above them and .pb in the flow, so the sheet reads as a
   stack of matching boxes. Without it the rows sat flush against the border
   (1px under the last one), which is why the block looked cut off rather than
   contained. */
.rev-rows.g-card{padding:${S[4]}px;border-radius:${R.lg}px}
.rev-row{display:flex;align-items:baseline;justify-content:space-between;gap:${S[4]}px}
.rev-row.big .rev-v{font-size:${F.sub}px;font-weight:800}
.rev-k{display:flex;align-items:center;gap:6px;font-size:13px;color:${C.textSub}}
.rev-v{font-family:${FONT.numeric};font-size:14px;font-weight:700;color:${C.text};text-align:right}
.rev-div{height:1px;background:${C.hairline};margin:${S[1]}px 0}
.rev-vit{display:flex;flex-direction:column;gap:${S[2]}px}
.rev-vhd{display:flex;align-items:center;justify-content:space-between;gap:${S[3]}px;flex-wrap:wrap}
.vit-chip{margin-left:6px;padding:2px 8px;border-radius:${R.pill}px;font-family:${FONT.numeric};
  font-size:10px;font-weight:700;color:${C.teal};background:rgba(26,157,139,.12)}
.vit-panel{display:flex;flex-direction:column;gap:6px;padding:${S[3]}px;border-radius:${R.sm}px;
  background:rgba(26,157,139,.07);border:1px solid rgba(26,157,139,.15)}
.vit-line{display:flex;justify-content:space-between;gap:${S[3]}px;font-size:12px;color:${C.textSub}}
.vit-line.ok{color:${C.ok};font-weight:700}

/* ── Success ────────────────────────────────────────────────────────────── */
.succ{border-radius:${R.lg}px;padding:${S[8]}px ${S[5]}px ${S[5]}px;display:flex;flex-direction:column;
  align-items:center;gap:${S[3]}px;text-align:center}
.succ-ring{width:64px;height:64px;border-radius:${R.pill}px;display:flex;align-items:center;justify-content:center;
  background:linear-gradient(150deg,${C.teal},${C.tealDeep});
  box-shadow:0 12px 28px -12px rgba(10,96,85,.8),0 0 0 8px rgba(26,157,139,.10)}
.succ-t{margin:${S[2]}px 0 0;font-size:${F.title}px;font-weight:800;letter-spacing:-.03em;color:${C.ink}}
.succ-p{margin:0;font-family:${FONT.numeric};font-size:13px;line-height:1.6;color:${C.textSub};max-width:40ch}
.succ-rows{width:100%;display:flex;flex-direction:column;gap:${S[3]}px;padding:${S[4]}px;border-radius:${R.md}px;
  background:rgba(11,46,44,.035);margin:${S[2]}px 0}

/* ── Kat ────────────────────────────────────────────────────────────────── */
.kat-scene{position:relative;width:100%;max-width:420px;margin:0 auto;aspect-ratio:1.586;perspective:1400px}
.kat-scene.frozen{filter:saturate(.3) brightness(.95)}
.kat-flip{position:absolute;inset:0;transform-style:preserve-3d}
.kat-face{position:absolute;inset:0;border-radius:${R.xl}px;overflow:hidden;cursor:pointer;
  backface-visibility:hidden;-webkit-backface-visibility:hidden;padding:${S[5]}px;
  display:flex;flex-direction:column;color:${C.onDark}}
.kat-back{transform:rotateY(180deg)}
.kat-top{display:flex;align-items:center;gap:${S[2]}px}
.kat-chip{padding:3px 9px;border-radius:${R.pill}px;background:rgba(255,255,255,.16);
  border:1px solid rgba(255,255,255,.22);font-size:10px;font-weight:700;letter-spacing:.08em;text-transform:uppercase}
.kat-pan{margin-top:auto;font-family:${FONT.numeric};font-size:24px;font-weight:300;letter-spacing:.05em}
.kat-bot{margin-top:${S[4]}px;display:flex;flex-direction:column;gap:${S[2]}px}
.kat-nm{font-size:15px;font-weight:500;letter-spacing:.06em}
.kat-meta{display:flex;gap:${S[8]}px}
.kat-meta span{display:flex;flex-direction:column;gap:2px}
.kat-meta i{font-style:normal;font-size:9px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;color:${C.onDarkMute}}
.kat-meta b{font-weight:400;font-size:14px;letter-spacing:.08em;color:${C.onDarkSub}}
.kat-brand{position:absolute;right:${S[5]}px;bottom:${S[5]}px;display:flex;align-items:center;gap:6px;
  font-size:19px;font-weight:800;letter-spacing:-.03em}
.kat-stripe{position:absolute;left:0;right:0;top:34px;height:52px;background:#04211E}
.kat-sig{margin-top:96px;display:flex;align-items:center;height:34px;border-radius:${R.xs}px;overflow:hidden;
  background:#EFF6F4;color:${C.ink}}
.kat-sig span{flex:1;height:100%;background:repeating-linear-gradient(115deg,rgba(11,46,44,.09) 0 2px,transparent 2px 9px)}
.kat-sig b{padding:0 ${S[3]}px;font-weight:700;font-size:13px;letter-spacing:.1em}
.kat-acts{display:flex;gap:${S[2]}px;margin-top:${S[3]}px}
.kat-b{flex:1;min-width:0;display:inline-flex;align-items:center;justify-content:center;gap:6px;
  padding:10px 6px;border-radius:${R.sm}px;cursor:pointer;font-family:${FONT.display};font-size:12px;font-weight:600;
  color:${C.onDark};border:1px solid rgba(255,255,255,.18);background:rgba(255,255,255,.10)}
.kat-b.on{background:rgba(255,255,255,.9);color:${C.ink};border-color:transparent}
.kat-hint{margin:0;text-align:center;font-size:12px;color:${C.textMute}}

/* ── Profil ─────────────────────────────────────────────────────────────── */
/* ── Pwofil ─────────────────────────────────────────────────────────────── */
.pf-hd{border-radius:${R.lg}px;padding:${S[6]}px ${S[5]}px ${S[5]}px;display:flex;
  flex-direction:column;align-items:center;gap:${S[2]}px;text-align:center}
.pf-av{position:relative;display:flex;margin-bottom:${S[2]}px}
.pf-badge{position:absolute;right:2px;bottom:2px;width:30px;height:30px;border-radius:${R.pill}px;
  background:linear-gradient(150deg,${C.tealBright},${C.teal});display:flex;align-items:center;
  justify-content:center;border:3px solid rgba(247,255,254,.96);
  box-shadow:0 4px 10px -4px rgba(10,96,85,.8)}
.pf-n{font-size:${F.title}px;font-weight:800;letter-spacing:-.03em;color:${C.ink};word-break:break-word}
.pf-e{font-family:${FONT.numeric};font-size:13px;color:${C.textMute};word-break:break-all}
.pf-verif{display:inline-flex;align-items:center;gap:7px;margin-top:${S[2]}px;padding:9px 17px;
  border-radius:${R.pill}px;font-size:14px;font-weight:700;color:${C.ok};
  background:${C.okSoft};border:1px solid rgba(12,138,82,.16)}

.pf-grid{position:relative;display:grid;grid-template-columns:1fr 1fr;gap:${S[2]}px}
.pf-stat{border-radius:${R.lg}px;padding:${S[4]}px ${S[4]}px ${S[5]}px;display:flex;
  flex-direction:column;gap:${S[2]}px;min-width:0}
.pf-statv{font-family:${FONT.numeric};font-size:${F.display}px;font-weight:800;letter-spacing:-.04em;
  color:${C.ink};overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.pf-card{border-radius:${R.lg}px;padding:${S[5]}px;display:flex;flex-direction:column;gap:${S[3]}px}
.pf-note{margin:0;font-size:13px;color:${C.textSub};line-height:1.5}
.pf-svc{display:flex;gap:${S[2]}px;flex-wrap:wrap}
.pf-pill{flex:1;min-width:150px;display:inline-flex;align-items:center;justify-content:center;gap:7px;
  padding:11px 14px;border-radius:${R.pill}px;font-size:13px;font-weight:700;color:${C.tealDeep};
  background:rgba(26,157,139,.11);border:1px solid rgba(26,157,139,.16)}
.pf-dot{width:8px;height:8px;border-radius:${R.pill}px;background:${C.teal};flex-shrink:0}
.pf-div{height:1px;background:${C.hairline}}
.pf-row{display:flex;align-items:baseline;justify-content:space-between;gap:${S[4]}px;font-size:14px;
  color:${C.textSub}}
.pf-row span:last-child{font-weight:700;color:${C.ink};font-size:15px}
.pf-avrow{display:flex;align-items:center;gap:${S[4]}px;flex-wrap:nowrap}
/* Bigger than the balance-card pill and evenly divided: four options that
   each get the same width, on a light frosted well rather than the dark one
   the hero uses. */
.pf-seg{flex:1;min-width:0;padding:6px;
  background:linear-gradient(158deg,rgba(255,255,255,.86) 0%,rgba(238,255,251,.66) 100%);
  border:1px solid rgba(255,255,255,.9);
  box-shadow:inset 0 1px 0 rgba(255,255,255,1),0 2px 8px -4px rgba(7,40,38,.16)}
.pf-seg .seg-b{flex:1 1 0;min-width:0;text-align:center;font-size:15px;font-weight:700;
  padding:11px 6px;min-height:46px;color:${C.textMute};
  display:flex;align-items:center;justify-content:center}
.pf-seg .seg-b.on{color:#fff}
@media (max-width:400px){
  .pf-avrow{gap:${S[3]}px}
  .pf-seg .seg-b{font-size:13.5px;padding:10px 4px;min-height:42px}
}

.pf-act{display:flex;align-items:center;justify-content:center;gap:${S[3]}px;
  border-radius:${R.lg}px;padding:${S[5]}px;cursor:pointer;min-height:60px;
  font-size:16px;font-weight:700;color:${C.tealDeep}}
.pf-danger{color:${C.danger}}

/* ── Sheets ─────────────────────────────────────────────────────────────── */
.scrim{position:fixed;inset:0;z-index:60;display:flex;align-items:flex-end;justify-content:center;
  background:rgba(7,40,38,.34);backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px)}
.sheet{width:100%;max-width:480px;border-radius:${R.xl}px ${R.xl}px 0 0;border-bottom:none;
  padding:${S[3]}px ${S[5]}px calc(${S[6]}px + env(safe-area-inset-bottom));max-height:88vh;overflow-y:auto;
  display:flex;flex-direction:column;gap:${S[4]}px}
.grab{width:40px;height:4px;border-radius:${R.pill}px;background:rgba(11,46,44,.14);margin:0 auto ${S[1]}px}
/* Identity + amount + state, in one card. 16px inset, same as .trk and
   .rev-rows.g-card below it, so the sheet is a stack of matching boxes. */
.sheet-card{display:flex;flex-direction:column;gap:${S[3]}px;
  padding:${S[4]}px;border-radius:${R.lg}px}
.sheet-hd{display:flex;align-items:center;gap:${S[3]}px}
/* Pushed to the right of the identity row. NOT absolutely positioned: the
   sheet is the scroll container, so an out-of-flow button would scroll away
   from the corner it was pinned to. */
.sheet-hd>.ico{margin-left:auto;align-self:flex-start}
.sheet-nm{font-size:17px;font-weight:700;color:${C.ink}}
.sheet-sub{font-family:${FONT.numeric};font-size:12.5px;color:${C.textMute}}
.sheet-amt{font-family:${FONT.numeric};font-size:${F.hero}px;font-weight:800;letter-spacing:-.04em;
  color:${C.ok};text-align:center;margin-top:${S[1]}px}
.sheet-amt.out{color:${C.ink}}

/* ── Intro ──────────────────────────────────────────────────────────────── */
.intro{position:fixed;inset:0;z-index:100;display:flex;flex-direction:column;
  align-items:center;justify-content:center;gap:${S[10]}px;
  padding:${S[8]}px ${S[5]}px calc(${S[8]}px + env(safe-area-inset-bottom));
  background:${SITE_BG}}
/* The dashboard's wash, same three radial tokens — but masked top and bottom
   so the edges land on pure ${SITE_BG}. The screen has no chrome to hide a
   seam against, which is the whole reason the mask is here and not there. */
.intro-wash{position:absolute;inset:0;pointer-events:none;
  background:
    radial-gradient(46% 34% at 12% 22%, rgba(65,198,181,.30) 0%, transparent 70%),
    radial-gradient(40% 30% at 92% 38%, rgba(94,234,212,.24) 0%, transparent 72%),
    radial-gradient(52% 40% at 74% 78%, rgba(26,157,139,.20) 0%, transparent 74%);
  -webkit-mask-image:linear-gradient(180deg,transparent 0%,#000 18%,#000 82%,transparent 100%);
  mask-image:linear-gradient(180deg,transparent 0%,#000 18%,#000 82%,transparent 100%)}
/* The mark alone. No plate behind it — a container would be a second mark. */
.intro-body{position:relative;display:flex;align-items:center;justify-content:center;
  filter:drop-shadow(0 22px 44px rgba(10,96,85,.22))}
.intro-foot{position:relative;display:flex;flex-direction:column;align-items:center}

/* ── Intro CTA — a slider that is also a button ─────────────────────────── */
/* Width/height/padding come from the component so the geometry has exactly
   one source; only appearance lives here. */
.ic{position:relative;flex-shrink:0}

/* The track is glass, not a fill: the same recipe as .g-card — translucent,
   blurred, saturated, hairline brighter on the top and left where a lit edge
   would catch — but tinted from the background's own mint family instead of
   carrying brand green. It sits BEHIND the knob and belongs to the page. */
.ic-track{position:absolute;inset:0;border-radius:${R.pill}px;pointer-events:none;
  background:linear-gradient(158deg,rgba(94,234,212,.22) 0%,rgba(65,198,181,.13) 48%,
             rgba(255,255,255,.30) 100%);
  border:1px solid rgba(255,255,255,.55);
  border-top-color:rgba(255,255,255,.94);
  border-left-color:rgba(255,255,255,.80);
  backdrop-filter:blur(18px) saturate(185%);
  -webkit-backdrop-filter:blur(18px) saturate(185%);
  box-shadow:inset 0 1px 0 rgba(255,255,255,.92),inset 0 -1px 0 rgba(11,46,44,.05),
             0 12px 28px -16px rgba(7,40,38,.30)}

/* The lane the knob has not covered yet, and the hint inside it. */
.ic-lane{position:absolute;top:0;bottom:0;display:flex;align-items:center;justify-content:center;
  gap:1px;pointer-events:none}
.ic-chev{display:flex}

/* The knob. Its own width and height are animated, so they are absent here —
   a CSS width would fight the tween. Radius stays at the pill maximum, which
   is what lets the browser round the corners correctly at every intermediate
   width as the shape closes into a circle. */
.ic-knob{position:absolute;display:flex;align-items:center;justify-content:center;
  cursor:grab;padding:0;border:none;overflow:hidden;touch-action:pan-y;
  background:linear-gradient(150deg,${C.teal} 0%,${C.tealDeep} 100%);
  box-shadow:0 10px 20px -8px rgba(4,32,28,.72),inset 0 1px 0 rgba(255,255,255,.30),
             inset 0 -2px 5px rgba(0,0,0,.18)}
.ic-knob:active{cursor:grabbing}
.ic-knob:disabled{cursor:default}
.ic-knob:focus{outline:none}
.ic-knob:focus-visible{outline:none;
  box-shadow:0 0 0 4px rgba(26,157,139,.34),0 10px 20px -8px rgba(4,32,28,.72),
             inset 0 1px 0 rgba(255,255,255,.30)}
.ic-label{font-family:${FONT.display};font-size:17px;font-weight:700;letter-spacing:-.015em;
  color:#fff;white-space:nowrap;pointer-events:none}
/* The tick is inside the knob, centred on it — same object, nothing to
   cross-fade with. */
.ic-tick{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;
  pointer-events:none}
.ic-ring{position:absolute;inset:0;border-radius:${R.pill}px;
  border:2px solid rgba(255,255,255,.55);pointer-events:none}
.sr-live{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}

/* ── Method chooser (restored) ──────────────────────────────────────────── */
.chooser{display:flex;flex-direction:column;gap:${S[2]}px}
.opt{border-radius:${R.lg}px;padding:${S[4]}px;display:flex;align-items:center;gap:${S[3]}px;
  cursor:pointer;min-height:44px}
.opt-ico{width:42px;height:42px;border-radius:${R.sm}px;flex-shrink:0;display:flex;align-items:center;
  justify-content:center;background:rgba(26,157,139,.12);border:1px solid rgba(26,157,139,.18)}
.opt-mid{flex:1;min-width:0}
.opt-t{font-size:14.5px;font-weight:700;color:${C.text}}
.opt-s{font-size:12px;color:${C.textMute};line-height:1.45;margin-top:2px}
.opt-badge{font-family:${FONT.numeric};font-size:9.5px;font-weight:700;letter-spacing:.06em;
  padding:3px 8px;border-radius:${R.pill}px;background:rgba(26,157,139,.12);color:${C.tealDeep};
  white-space:nowrap;flex-shrink:0}

/* ── Type chooser ───────────────────────────────────────────────────────── */
/* Fills the page instead of sitting in a short box with dead space under it.
   The cards centre in whatever height is left, so the screen reads as one
   surface rather than a panel floating above an empty half. */
.tc{display:flex;flex-direction:column;justify-content:center;gap:${S[6]}px;
  padding-top:${S[4]}px;flex:1}
/* overflow:hidden is what makes the carousel a carousel — but it clips on
   every edge, and the cards throw a 22px drop shadow. Clipping that shadow
   flat is the hard horizontal line that showed up under the last row. The
   horizontal bleed (negative margin + matching padding) already solved this
   sideways; the same trick vertically gives the shadow room to land inside
   the clip box instead of being cut by it. */
.tc-vp{position:relative;overflow:hidden;
  margin:-${S[6]}px -${S[5]}px;padding:${S[6]}px ${S[5]}px}
.tc-track{display:flex;width:100%}
.tc-card{flex:0 0 100%;min-width:0;display:flex;flex-direction:column;align-items:center;
  text-align:center;gap:${S[2]}px;padding:0 2px}
.tc-tile{width:110px;height:110px;border-radius:34px;display:flex;align-items:center;justify-content:center;
  margin-bottom:${S[4]}px}
.tc-tile svg{width:46px;height:46px}
.tc-t{margin:0;font-size:${F.display}px;font-weight:800;letter-spacing:-.03em;text-transform:uppercase;
  color:${C.tealDeep};line-height:1.05}
.tc-s{margin:0;max-width:26ch;font-size:15px;color:${C.textSub};line-height:1.5}
.tc-rows{width:100%;display:flex;flex-direction:column;gap:${S[3]}px;margin-top:${S[6]}px}
.tc-row{display:flex;align-items:center;gap:${S[4]}px;padding:${S[4]}px;border-radius:${R.lg}px;
  cursor:pointer;text-align:left;min-height:44px}
.tc-rico{width:52px;height:52px;border-radius:18px;flex-shrink:0;display:flex;align-items:center;
  justify-content:center;background:linear-gradient(150deg,${C.tealBright} 0%,${C.teal} 55%,${C.tealDeep} 100%);
  box-shadow:0 8px 18px -8px rgba(10,96,85,.7),inset 0 1px 0 rgba(255,255,255,.3)}
.tc-rmid{flex:1;min-width:0}
.tc-rl{font-size:15px;font-weight:800;letter-spacing:-.01em;text-transform:uppercase;color:${C.tealDeep}}
.tc-rd{font-size:13px;color:${C.textSub};line-height:1.45;margin-top:3px}

.tc-dots{display:flex;align-items:center;justify-content:center;gap:${S[2]}px}
.tc-dot{position:relative;width:14px;height:7px;border-radius:${R.pill}px;border:none;cursor:pointer;
  padding:0;background:rgba(26,157,139,.20);transition:width .26s cubic-bezier(.22,1,.36,1)}
.tc-dot.on{width:38px;background:transparent}
.tc-dotfill{position:absolute;inset:0;border-radius:${R.pill}px;background:${C.teal}}

/* Arrows are a pointer affordance — hidden below the desktop breakpoint,
   where the gesture is the interaction. */
.tc-arrow{display:none}
@media (min-width:${BP.desktop}px){
  .tc-arrow{position:absolute;top:64px;z-index:3;width:44px;height:44px;border-radius:${R.pill}px;
    display:flex;align-items:center;justify-content:center;cursor:pointer;padding:0}
  .tc-prev{left:-4px}
  .tc-next{right:-4px}
  .tc-vp{margin:-${S[6]}px -${S[8]}px;padding:${S[6]}px ${S[8]}px}
}

/* ── Live summary (restored) ────────────────────────────────────────────── */
.live{border-radius:${R.lg}px;padding:${S[4]}px ${S[5]}px;display:flex;flex-direction:column;gap:${S[1]}px}
.live-hd{display:flex;align-items:center;justify-content:space-between;gap:${S[3]}px}
.live-recip{display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:600;color:${C.textSub}}
.live-big{font-size:${F.display}px;font-weight:800;letter-spacing:-.04em;color:${C.text};margin-top:${S[1]}px}
.live-big span{font-size:15px;font-weight:700;color:${C.textMute};margin-left:3px}
.live-sub{font-size:11.5px;color:${C.textMute}}
.live-rows{display:flex;flex-direction:column;gap:7px;margin-top:${S[3]}px;padding-top:${S[3]}px;
  border-top:1px solid ${C.hairline}}
.live-row{display:flex;justify-content:space-between;gap:${S[3]}px;font-size:12.5px;color:${C.textSub}}
.live-row.total{color:${C.text};font-weight:700;font-size:13.5px}

/* ── Processing popup ───────────────────────────────────────────────────── */
.proc-scrim{align-items:center;padding:${S[6]}px}
.proc{width:100%;max-width:340px;border-radius:${R.xl}px;padding:${S[8]}px ${S[6]}px ${S[6]}px;
  border-bottom:1px solid rgba(255,255,255,.72);max-height:none}
.proc-in{display:flex;flex-direction:column;align-items:center;gap:${S[2]}px;text-align:center}
.proc-t{margin:${S[3]}px 0 0;font-size:${F.title}px;font-weight:800;letter-spacing:-.03em;color:${C.ink}}
.proc-p{margin:0;font-size:13.5px;color:${C.textSub};max-width:28ch;line-height:1.55}
.proc-cta{width:100%;margin-top:${S[4]}px;flex:0 0 auto}
.proc-plane{position:relative;width:72px;height:72px;border-radius:${R.pill}px;display:flex;
  align-items:center;justify-content:center;background:rgba(26,157,139,.10);
  border:1px solid rgba(26,157,139,.18)}
.proc-trail{position:absolute;inset:-8px;border-radius:${R.pill}px;border:1px solid rgba(26,157,139,.18);
  animation:pulse 1.9s ease-in-out infinite}
@keyframes pulse{0%,100%{transform:scale(1);opacity:.7}50%{transform:scale(1.07);opacity:.25}}

/* Row highlight after following a transaction. */
.row.hi{background:rgba(26,157,139,.10);box-shadow:inset 3px 0 0 ${C.teal}}

.pv-av{border-radius:${R.pill}px;overflow:hidden;display:flex;align-items:center;justify-content:center;
  color:#fff;font-weight:800;font-family:${FONT.numeric};flex-shrink:0;letter-spacing:-.02em}
.pv-av img{width:100%;height:100%;display:block;border-radius:${R.pill}px}

/* ── Nav ────────────────────────────────────────────────────────────────── */
/* Icons only, with the centre slot raised out of the bar. The bar keeps its
   own height so the raised button has something to sit proud of; every item
   still clears 44px because the row is 52 and the button is 60. */
/* The blur field the dock sits on.
 *
 * Without it the bar meets sharp content at a hard edge and the page reads as
 * clipped. This is a full-width band pinned to the same edge as the dock, so
 * the two travel as one unit, blurring whatever scrolls beneath.
 *
 * Geometry is derived from the dock, not guessed:
 *   bar bottom      14px            (+ safe area)
 *   bar height      62px            (5 + 52 + 5)      -> bar top at 76px
 *   home button     ~26px proud of the bar + a 6px ring -> ~89px
 * The band is 140px tall, so in mask coordinates the bar top lands at 44%
 * and the button's top at 36%. The mask is therefore fully opaque by 34% —
 * everything the dock actually occupies sits on solid blur — and ramps to
 * transparent above it, which is what makes the edge a fade and not a
 * rectangle.
 *
 * 140px and not more: the page's bottom padding puts the last control's
 * bottom edge 104px up, only 15px clear of the raised home button. A taller
 * band cannot cover the button without also covering that control, so the
 * band stops just above the dock and lets the ramp do the rest.
 *
 * The mask applies to the backdrop-filter too, so content genuinely softens
 * on its way under the bar rather than being cut at a line. */
.dock-veil{position:fixed;left:0;right:0;bottom:0;z-index:49;pointer-events:none;
  height:calc(140px + env(safe-area-inset-bottom));
  /* NO saturate(). The sheet tier boosts saturation to 190%, which on this
     pale ground pulled everything under the band toward mint — that tint was
     the filter, not the fill. A plain blur leaves colour alone.
     10px, not the sheet tier's 30px: this band passes over real controls on
     their way down the page (the flow's Kontinye, Profil's Dekonekte), and at
     30px they stopped being readable while still being tappable. 10px softens
     the meeting point without taking the content away. */
  backdrop-filter:blur(6px);
  -webkit-backdrop-filter:blur(6px);
  /* #EAFFFC — the page's own ground, so the band tints toward the background
     rather than toward the brand. */
  background:linear-gradient(180deg,rgba(234,255,252,0) 0%,rgba(234,255,252,.20) 55%,
             rgba(234,255,252,.52) 100%);
  mask-image:linear-gradient(180deg,transparent 0%,rgba(0,0,0,.26) 16%,
             rgba(0,0,0,.70) 27%,#000 34%,#000 100%);
  -webkit-mask-image:linear-gradient(180deg,transparent 0%,rgba(0,0,0,.26) 16%,
             rgba(0,0,0,.70) 27%,#000 34%,#000 100%)}
/* No blur where there is no backdrop-filter — a translucent band over sharp
   content would be worse than the hard edge it replaces. The gradient alone
   still softens the meeting point. */
@supports not ((backdrop-filter:blur(1px)) or (-webkit-backdrop-filter:blur(1px))){
  .dock-veil{background:linear-gradient(180deg,rgba(234,255,252,0) 0%,
             rgba(234,255,252,.86) 52%,${SITE_BG} 100%)}
}

.dock{position:fixed;left:50%;transform:translateX(-50%);bottom:calc(14px + env(safe-area-inset-bottom));
  z-index:50;display:flex;align-items:center;gap:2px;padding:5px;border-radius:${R.pill}px;
  width:calc(100% - 32px);max-width:400px;overflow:visible;
  /* Chip-tier rule, same as .hdr-mark: the veil behind it has already blurred
     this ground, and blurring it twice would make the bar milkier than its
     own surround — re-creating the pasted-on rectangle the veil exists to
     remove. The fill, border and shadow from .g-sheet all stay. */
  backdrop-filter:none;-webkit-backdrop-filter:none}
.dock-item{position:relative;flex:1;min-width:0;border:none;background:none;cursor:pointer;padding:0;
  border-radius:${R.pill}px;font-family:${FONT.display}}
/* Selected = a solid brand pill behind the icon. No label, on any item —
   the bar stays glyph-only and every slot keeps the same width, so nothing
   reflows as the selection moves. */
.dock-pill{position:absolute;inset:0;border-radius:${R.pill}px;
  background:linear-gradient(150deg,${C.teal} 0%,${C.tealDeep} 100%);
  box-shadow:0 8px 18px -8px rgba(10,96,85,.85),inset 0 1px 0 rgba(255,255,255,.26)}
.dock-in{position:relative;z-index:1;display:flex;align-items:center;justify-content:center;
  height:52px;color:${C.textMute}}
.dock-item.on .dock-in{color:#fff}

/* The raised home button. Negative margin pulls it up out of the bar; the
   ring is the bar's own ground so it reads as cut through rather than
   floating over. */
.dock-home{flex:0 0 auto;margin:-26px 6px 0}
.dock-home .dock-in{width:60px;height:60px;border-radius:${R.pill}px;color:#fff;
  background:linear-gradient(150deg,${C.teal} 0%,${C.tealDeep} 100%);
  box-shadow:0 0 0 6px rgba(247,255,254,.92),0 14px 26px -10px rgba(10,96,85,.85),
             inset 0 1px 0 rgba(255,255,255,.28)}
.dock-item.on.dock-home .dock-in{color:#fff}
.rail{display:none}

/* ── Notifications — its own page ───────────────────────────────────────── */
.notif-wrap{position:relative;flex-shrink:0;overflow:hidden}
.notif-dot{position:absolute;top:9px;right:10px;width:8px;height:8px;border-radius:${R.pill}px;
  background:${C.danger};box-shadow:0 0 0 2px rgba(247,255,254,.95)}

/* Page title. Larger than the shell header's, because on this screen it IS
   the screen rather than a label for it. */
.nt-title{margin:0;font-size:${F.display}px;font-weight:800;letter-spacing:-.035em;color:${C.ink}}
.nt-card{border-radius:${R.lg}px;padding:${S[2]}px 0;overflow:hidden}
/* Day headers sit inside the card, and the ONLY rule is between groups —
   rows inside a day run together so the eye reads days as blocks instead of
   counting dividers. */
.nt-day{font-size:12px;font-weight:700;color:${C.textSub};padding:${S[3]}px ${S[4]}px ${S[1]}px}
.nt-sep{height:1px;background:${C.hairline};margin:${S[2]}px 0 0}
.nt-row{display:flex;align-items:flex-start;gap:${S[3]}px;padding:${S[3]}px ${S[4]}px;
  text-align:left;width:100%;background:none;border:none;font-family:${FONT.display}}
.nt-row[role="button"]{cursor:pointer}
.nt-row[role="button"]:hover{background:rgba(11,46,44,.035)}
/* isolation:isolate keeps the badge's stacking inside the row rather than
   letting it interact with the card's own layers. */
.nt-ico{position:relative;isolation:isolate;width:44px;height:44px;flex-shrink:0;
  border-radius:${R.pill}px}
.nt-ico img{border-radius:${R.pill}px;display:block}
.nt-glyph{width:44px;height:44px;border-radius:${R.pill}px;display:flex;align-items:center;
  justify-content:center;background:rgba(11,46,44,.055);border:1px solid rgba(11,46,44,.05)}
.nt-badge{position:absolute;right:-2px;bottom:-2px;width:19px;height:19px;border-radius:${R.pill}px;
  display:flex;align-items:center;justify-content:center;z-index:2;
  box-shadow:0 0 0 2.5px ${C.card ?? "#F7FFFE"}}
.nt-b-in{background:${C.ok}}
.nt-b-out{background:${C.tealDeep}}
.nt-b-add{background:#2F6FD0}
.nt-b-ok{background:${C.teal}}
.nt-b-warn{background:${C.warn}}
.nt-mid{flex:1;min-width:0;display:flex;flex-direction:column;gap:2px;padding-top:1px}
.nt-t{font-size:14.5px;font-weight:600;color:${C.text};line-height:1.35}
.nt-b{font-size:13px;color:${C.textSub};line-height:1.4}
.nt-at{font-family:${FONT.numeric};font-size:12px;color:${C.textMute};margin-top:2px}

.draft-rows{width:100%;display:flex;flex-direction:column;gap:${S[2]}px;padding:${S[3]}px;
  border-radius:${R.sm}px;background:rgba(11,46,44,.04);margin:${S[3]}px 0 ${S[1]}px}
.draft-ghost{width:100%;margin-top:${S[2]}px}

/* ── Toast ──────────────────────────────────────────────────────────────── */
/* Centred WITHOUT a transform. Same trap as the swap arrow: this is a
   motion.div, so framer-motion writes its own transform for the entry
   animation and replaces translateX(-50%) — which is why it sat 99px off
   centre. left/right + margin:auto cannot be overwritten by an animation. */
.toast{position:fixed;left:0;right:0;margin:0 auto;width:fit-content;
  bottom:calc(88px + env(safe-area-inset-bottom));
  z-index:80;display:flex;align-items:center;gap:${S[2]}px;padding:11px 18px;border-radius:${R.pill}px;
  color:${C.onDark};font-size:13.5px;font-weight:600;max-width:calc(100% - 40px)}

/* ── Skeleton ───────────────────────────────────────────────────────────── */
.sk{background:linear-gradient(100deg,rgba(11,46,44,.055) 30%,rgba(11,46,44,.10) 50%,rgba(11,46,44,.055) 70%);
  background-size:220% 100%;animation:shim 1.5s ease-in-out infinite}
@keyframes shim{0%{background-position:120% 0}100%{background-position:-40% 0}}

/* ── Desktop: the dock becomes a rail. Same items, same component. ─────── */
@media (min-width:${BP.desktop}px){
  /* The rail replaces the dock here, so the field it sat on goes with it. */
  .dock{display:none}
  .dock-veil{display:none}
  /* The bar is full-bleed on desktop too.
     It sits inside .col, which begins after the rail, so position:absolute
     pinned it to the content column and left the strip above the sidebar
     bare. position:fixed spans the viewport instead — nothing on the way up
     creates a containing block for it, so it anchors where it should. Its
     CONTENT still starts at
     the content column's left edge (rail margin + rail width + the bar's own
     padding), so the title stays on the same vertical as everything below it
     while the surface runs edge to edge. */
  .hdr{position:fixed;left:0;right:0;top:0}
  /* The rail starts below the bar rather than under it. Its margin, height and
     sticky offset all move together, or it either overlaps the bar or pushes
     the page past 100vh. */
  .rail{display:flex;flex-direction:column;gap:${S[6]}px;width:250px;flex-shrink:0;
    margin:calc(var(--hdr-h) + ${S[4]}px) 0 ${S[4]}px ${S[4]}px;border-radius:${R.xl}px;
    padding:${S[5]}px ${S[4]}px;
    height:calc(100vh - var(--hdr-h) - ${S[8]}px);position:sticky;
    top:calc(var(--hdr-h) + ${S[4]}px)}
  .rail-brand{display:flex;align-items:center;gap:${S[2]}px;font-size:20px;font-weight:800;
    letter-spacing:-.03em;color:${C.ink};padding:0 ${S[2]}px}
  .rail-nav{display:flex;flex-direction:column;gap:${S[1]}px}
  .rail-item{position:relative;border:none;background:none;cursor:pointer;padding:0;border-radius:${R.sm}px;
    font-family:${FONT.display};text-align:left}
  .rail-pill{position:absolute;inset:0;border-radius:${R.sm}px;background:${C.teal};
    box-shadow:0 8px 18px -10px rgba(10,96,85,.9)}
  .rail-in{position:relative;z-index:1;display:flex;align-items:center;gap:${S[3]}px;padding:11px ${S[3]}px;
    font-size:14px;font-weight:600;color:${C.textSub}}
  .rail-item.on .rail-in{color:#fff}
  .rail-foot{margin-top:auto;display:flex;align-items:center;gap:${S[3]}px;padding:${S[3]}px;
    border-radius:${R.md}px;background:rgba(11,46,44,.04);min-width:0}
  .rail-nm{font-size:13px;font-weight:700;color:${C.text};overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .rail-em{font-family:${FONT.numeric};font-size:11px;color:${C.textMute};overflow:hidden;
    text-overflow:ellipsis;white-space:nowrap}
  /* --hdr-h is the single source for the bar's height: the rail's top margin,
     its height and its sticky offset are all derived from it, so the bar and
     the sidebar can never disagree about where one ends and the other starts.
     The left padding puts the title on the content column's edge (rail margin
     + rail width + the page's own gutter) while the surface stays full-bleed. */
  .shell{--hdr-h:88px}
  .hdr{height:var(--hdr-h);
       padding:0 ${S[8]}px 0 calc(${S[4]}px + 250px + ${S[8]}px)}
  .hdr-l,.hdr-r{max-width:none}
  .scroll{padding-top:var(--hdr-h)}
  .hdr-mark{width:44px;height:44px}
  .hdr-mark{display:none}
  .hdr-t{font-size:${F.display}px}
  .page{padding:${S[8]}px ${S[8]}px ${S[10]}px;max-width:640px;margin:0 auto}
  .scrim{align-items:center;padding:${S[5]}px}
  .sheet{border-radius:${R.xl}px;border-bottom:1px solid rgba(255,255,255,.72);max-height:84vh}
  /* Centred on desktop too. A toast pinned to a corner is a notification; a
     toast where the eye already is, is feedback. */
  .toast{left:0;right:0;margin:0 auto;bottom:auto;top:${S[6]}px}
}
@media (min-width:${BP.wide}px){ .page{max-width:720px} }

/* Below tablet width, the desktop-scale hero/display figures and section
   titles read as oversized on an actual phone. Trims the type scale ~12%
   off the (now-reduced) desktop F values, and the page's own gutters a
   notch, mobile only; the desktop rules above are untouched. */
@media (max-width:${BP.tablet - 1}px){
  .hdr-t{font-size:18px}
  .hero-num,.sheet-amt{font-size:26px}
  .pb-big,.kf-t,.tc-t,.live-big,.nt-title,.pf-statv{font-size:19px}
  .stat-v,.vfy-num,.kf-refnum,.rev-row.big .rev-v{font-size:15px}
  .succ-t,.pf-n,.proc-t{font-size:18px}
  .page{padding:${S[5]}px ${S[4]}px calc(104px + env(safe-area-inset-bottom))}
}

@media (prefers-reduced-motion:reduce){
  *{animation-duration:.01ms !important;transition-duration:.01ms !important}
  .kat-flip{transform:none !important}
  /* An infinite animation clamped to .01ms still runs forever — it just runs
     fast. The pulse has to be removed outright, not shortened. */
  .trk-dot.beat,.trk-node.beat{animation:none !important}
}
`
